import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { usePollUntilPendingBrandPlanSchedules } from 'hooks/queries/schedules/usePollUntilPendingBrandPlanSchedules'
import { useMutation, useQueryClient, type QueryKey } from 'react-query'
import { ApiError, reportError } from 'utils/error'
import { scheduleKeys } from '../schedules/scheduleKeys'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { type AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { type AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { type BrandPlanSchedule, type BrandPlanScheduleContent } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export type ContentScope = 'current_batch' | 'user_selected_content' | 'upcoming_week_content'

export interface RegenerateContentOverride {
  file_id: string
  topic?: string
  media_files?: { id: string }[]
  sources?: { type: string; id: string; save_media?: boolean }[]
  avatar_id?: number
  avatar_angle?: string
  avatar_script?: string
  avatar_captions?: boolean
  avatar_caption_style?: string
  video_caption_style?: string
  video_voiceover_voice?: string
}

export interface RegenerateBody {
  topic?: string
  file_ids?: string[]
  initial_media_file_ids?: string[]
  content_scope?: ContentScope
  contents?: RegenerateContentOverride[]
}

const regenerateContent = async ({
  workspaceId,
  brandPlanId,
  body,
}: {
  workspaceId: number
  brandPlanId: number
  body: RegenerateBody
}) => {
  const res = await sendToApi(`/w/${workspaceId}/brand_plans/${brandPlanId}/regenerate_content`, {
    method: 'POST',
    body,
  })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    const errorMessage =
      Array.isArray(res.data?.errors) && typeof res.data.errors[0] === 'string'
        ? res.data.errors.join('. ')
        : 'An error occurred! Our team has been notified and we are looking into it.'
    throw new ApiError(errorMessage, res.data?.error_code)
  }
  return res.data
}

export interface RegenerateReplacement {
  replaced_file_id: string
  content: BrandPlanScheduleContent
  almanac_file_index?: AlmanacFileIndexItem
}

export type RegenerateBrandPlanContentResponse = {
  success: boolean
  regenerated_count: number
  message: string
  content_ids?: number[]
  schedule_id?: number
  replacements?: RegenerateReplacement[]
}

interface UseRegenerateBrandPlanContentOptions {
  onError?: (error: Error) => void
  onSuccess?: (data: RegenerateBrandPlanContentResponse) => void
  onSettled?: () => void
}

const applyReplacements = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
  replacements: RegenerateReplacement[],
): void => {
  if (replacements.length === 0) return

  const byReplacedId = new Map(replacements.map((r) => [r.replaced_file_id, r.content]))
  const replacedFileIds = new Set(replacements.map((r) => r.replaced_file_id))

  const scheduleCaches = queryClient.getQueriesData<BrandPlanSchedule>(scheduleKeys.details())
  scheduleCaches.forEach(([queryKey, schedule]) => {
    const contents = schedule?.contents
    if (!Array.isArray(contents)) return

    const seen = new Set<number>()
    const next: BrandPlanScheduleContent[] = []
    let dirty = false

    contents.forEach((c: BrandPlanScheduleContent) => {
      const replacement = c.almanac_file?.key ? byReplacedId.get(c.almanac_file.key) : undefined
      const item = replacement ?? c
      if (replacement) {
        dirty = true
      }
      if (seen.has(item.id)) {
        dirty = true
        return
      }
      seen.add(item.id)
      next.push(item)
    })

    if (!dirty) return
    queryClient.setQueryData(queryKey, { ...schedule, contents: next })
  })

  const removeFromArrayCache = (prefix: QueryKey) => {
    const caches = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(prefix)
    caches.forEach(([queryKey, files]) => {
      if (!Array.isArray(files)) return
      const next = files.filter((f) => !replacedFileIds.has(f.key))
      if (next.length === files.length) return
      queryClient.setQueryData(queryKey, next)
    })
  }
  removeFromArrayCache(calendarKeys.all)

  const newCalendarFilesByReplacedId = new Map(
    replacements
      .filter((r): r is RegenerateReplacement & { almanac_file_index: AlmanacFileIndexItem } => {
        return !!r.almanac_file_index
      })
      .map((r) => [r.replaced_file_id, r.almanac_file_index]),
  )
  const postAtMs = (af: AlmanacFileIndexItem): number =>
    af.post_at ? new Date(af.post_at).getTime() : Number.POSITIVE_INFINITY
  const hasSamePostAt = (a: AlmanacFileIndexItem, b: AlmanacFileIndexItem): boolean => postAtMs(a) === postAtMs(b)
  const insertByServerPostAt = (
    files: AlmanacFileIndexItem[],
    seen: Set<string>,
    af: AlmanacFileIndexItem,
  ): boolean => {
    if (seen.has(af.key)) return false
    seen.add(af.key)
    const insertAt = files.findIndex((file) => postAtMs(file) > postAtMs(af))
    if (insertAt === -1) {
      files.push(af)
    } else {
      files.splice(insertAt, 0, af)
    }
    return true
  }
  const partialCalendarCaches = queryClient.getQueriesData<AlmanacFileIndexItem[]>(
    filesKeys.partialCalendar(workspaceId),
  )
  partialCalendarCaches.forEach(([queryKey, files]) => {
    if (!Array.isArray(files)) return
    const seen = new Set<string>()
    const next: AlmanacFileIndexItem[] = []
    const displacedFiles: AlmanacFileIndexItem[] = []
    let dirty = false

    files.forEach((f) => {
      const replacement = newCalendarFilesByReplacedId.get(f.key)
      if (replacement) {
        dirty = true
        if (hasSamePostAt(f, replacement)) {
          if (!seen.has(replacement.key)) {
            seen.add(replacement.key)
            next.push(replacement)
          }
        } else {
          displacedFiles.push(replacement)
        }
        return
      }
      if (replacedFileIds.has(f.key)) {
        dirty = true
        return
      }
      if (seen.has(f.key)) {
        dirty = true
        return
      }
      seen.add(f.key)
      next.push(f)
    })

    displacedFiles.forEach((af) => {
      dirty = insertByServerPostAt(next, seen, af) || dirty
    })

    if (!dirty) {
      return
    }
    queryClient.setQueryData(queryKey, next)
  })
}

export const useRegenerateBrandPlanContent = (options?: UseRegenerateBrandPlanContentOptions) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})
  const calendarV2 = useFlipperFlag('calendar_v2')

  return useMutation<RegenerateBrandPlanContentResponse, Error, { brandPlanId: number; body: RegenerateBody }>(
    ({ brandPlanId, body }) => regenerateContent({ workspaceId, brandPlanId, body }),
    {
      onSuccess: (data) => {
        if (calendarV2) {
          applyReplacements(queryClient, workspaceId, data.replacements ?? [])
        }

        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        if (data.schedule_id) {
          if (calendarV2) {
            queryClient.invalidateQueries(scheduleKeys.activeBatch(data.schedule_id))
          } else {
            queryClient.removeQueries(scheduleKeys.detail(data.schedule_id))
          }
        }
        pollUntilPendingSchedules()
        options?.onSuccess?.(data)
      },
      onError: (error) => {
        reportError(error)
        options?.onError?.(error)
      },
      onSettled: options?.onSettled,
    },
  )
}
